import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { AVAILABLE_VARIABLES } from '../utils/templateHelper';

    const { FiX, FiPlus, FiEdit, FiTrash2, FiSave, FiMail, FiMessageSquare, FiCode, FiLock } = FiIcons;

    const TemplateManager = ({ onClose }) => {
      const { messageTemplates, addTemplate, updateTemplate, deleteTemplate } = useData();
      const [activeTab, setActiveTab] = useState('email'); // 'email' or 'sms'
      const [editingTemplate, setEditingTemplate] = useState(null);
      const [isCreating, setIsCreating] = useState(false);

      // Form State
      const [formData, setFormData] = useState({
        name: '',
        subject: '',
        body: ''
      });

      const filteredTemplates = messageTemplates.filter(t => t.type === activeTab);

      const handleCreate = () => {
        setFormData({ name: '', subject: '', body: '' });
        setIsCreating(true);
        setEditingTemplate(null);
      };

      const handleEdit = (template) => {
        setFormData({
          name: template.name,
          subject: template.subject || '',
          body: template.body
        });
        setEditingTemplate(template);
        setIsCreating(false);
      };

      const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
          await deleteTemplate(id);
          if (editingTemplate?.id === id) {
            setEditingTemplate(null);
            setIsCreating(false);
          }
        }
      };

      const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.body) {
          alert('Name and Body are required');
          return;
        }

        const templateData = {
          type: activeTab,
          name: formData.name,
          body: formData.body,
          subject: activeTab === 'email' ? formData.subject : null
        };

        if (editingTemplate) {
          await updateTemplate(editingTemplate.id, templateData);
        } else {
          await addTemplate(templateData);
        }
        setEditingTemplate(null);
        setIsCreating(false);
      };

      const insertVariable = (variable) => {
        setFormData(prev => ({
          ...prev,
          body: prev.body + variable
        }));
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-white rounded-xl shadow-xl max-w-6xl w-full h-[85vh] flex overflow-hidden"
          >
            {/* Sidebar - List */}
            <div className="w-1/3 border-r bg-gray-50 flex flex-col">
              <div className="p-4 border-b bg-white">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Templates</h2>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={FiX} className="text-xl" />
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-lg mb-4">
                  <button 
                    onClick={() => { setActiveTab('email'); setEditingTemplate(null); setIsCreating(false); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${
                      activeTab === 'email' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <SafeIcon icon={FiMail} /> Email
                  </button>
                  <button 
                    onClick={() => { setActiveTab('sms'); setEditingTemplate(null); setIsCreating(false); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${
                      activeTab === 'sms' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <SafeIcon icon={FiMessageSquare} /> SMS
                  </button>
                </div>

                <button 
                  onClick={handleCreate}
                  className="w-full flex items-center justify-center gap-2 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <SafeIcon icon={FiPlus} /> Create New Template
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {filteredTemplates.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm py-4">No templates found.</p>
                ) : (
                  filteredTemplates.map(template => (
                    <div 
                      key={template.id} 
                      onClick={() => handleEdit(template)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        editingTemplate?.id === template.id 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{template.name}</h3>
                          {template.isSystem && (
                            <span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded flex items-center gap-1" title="System Template">
                              <SafeIcon icon={FiLock} className="text-[10px]" /> System
                            </span>
                          )}
                        </div>
                        {!template.isSystem && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDelete(template.id); }}
                            className="text-gray-400 hover:text-red-500 p-1"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.body}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Main Content - Editor */}
            <div className="flex-1 flex flex-col bg-white">
              {(editingTemplate || isCreating) ? (
                <form onSubmit={handleSave} className="flex-1 flex flex-col h-full">
                  <div className="p-6 border-b flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {isCreating ? 'New Template' : 'Edit Template'}
                    </h3>
                    {editingTemplate?.isSystem && (
                      <div className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-lg flex items-center gap-2">
                        <SafeIcon icon={FiLock} />
                        System templates cannot be renamed or deleted
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                      <input 
                        type="text" 
                        value={formData.name} 
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none ${
                          editingTemplate?.isSystem ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300'
                        }`}
                        placeholder="e.g., Monthly Payment Reminder"
                        required
                        disabled={editingTemplate?.isSystem}
                      />
                    </div>

                    {activeTab === 'email' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Subject</label>
                        <input 
                          type="text" 
                          value={formData.subject} 
                          onChange={e => setFormData({...formData, subject: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                          placeholder="e.g., Invoice for {studentName}"
                        />
                      </div>
                    )}

                    <div className="flex-1 flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message Content</label>
                      
                      {/* Variables Toolbar */}
                      <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg border">
                        <span className="flex items-center gap-1 text-xs font-medium text-gray-500 mr-2">
                          <SafeIcon icon={FiCode} /> Variables:
                        </span>
                        {AVAILABLE_VARIABLES.map(variable => (
                          <button 
                            key={variable.value}
                            type="button"
                            onClick={() => insertVariable(variable.value)}
                            className="px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-colors"
                          >
                            {variable.label}
                          </button>
                        ))}
                      </div>

                      <textarea 
                        value={formData.body} 
                        onChange={e => setFormData({...formData, body: e.target.value})}
                        className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-mono text-sm min-h-[300px]"
                        placeholder={`Hello {parentName},\n\nThis is a reminder...`}
                        required
                      />
                    </div>
                  </div>

                  <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                    <button 
                      type="button"
                      onClick={() => { setEditingTemplate(null); setIsCreating(false); }}
                      className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      <SafeIcon icon={FiSave} /> Save Template
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                  <SafeIcon icon={activeTab === 'email' ? FiMail : FiMessageSquare} className="text-6xl mb-4 text-gray-200" />
                  <p className="text-lg font-medium text-gray-500">Select a template to edit</p>
                  <p className="text-sm">or create a new one to get started</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      );
    };

    export default TemplateManager;